import React, { useEffect, useState } from 'react';
import { EditIcon, MessageEnvelope } from '../../../components/icons';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { CurrentUserProfile } from '../../../interfaces/User';
import { Country } from '../../../interfaces/Country';
import { BusinessType } from '../../../customhooks/businesstypes/useBusinesstypes';
import { Industry } from '../../../customhooks/industries/useIndustries';
import { mutateConfig } from '../../../api/queryBase';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { getErrorMessage } from '../../../utils/helper';
import { Divider, Select } from 'antd';
import Input from '../../../components/input/Input';
import Button from '../../../components/button/Button';
import CountriesDropdown from '../../../components/CountriesDropdown';

const ProfileAccountDetails = ({
    userDetails,
    countries,
    isBusinessTypesLoading,
    businessTypes,
    industries,
}: {
    userDetails: CurrentUserProfile;
    countries: Country[];
    isCountriesLoading: boolean;
    isBusinessTypesLoading: boolean;
    businessTypes: BusinessType[];
    industries: Industry[];
    isIndustriesLoading: boolean;
}) => {
    const queryClient = useQueryClient();
    const mutation = mutateConfig({ method: 'patch' });
    const { mutate } = mutation({
        url: `/me`
    });

    const formik = useFormik({
        initialValues: {
            firstName: userDetails.firstName ?? '',
            lastName: userDetails.lastName ?? '',
            corporateCountryCode: userDetails?.corporate?.countryCode ?? '',
            industry: userDetails?.corporate?.industry ?? '',
            businessType: userDetails?.businessTypes ?? [],
            phoneNumber: userDetails?.phoneNumber ?? '',
            email: userDetails?.email ?? '',
            corporateName: userDetails?.corporate?.businessName ?? '',
            corporateIndustryId:
                industries?.find(
                    (item) => item.name === userDetails?.corporate?.industry
                )?.id ?? ''
        },
        validationSchema: object({
            lastName: string().required(),
            firstName: string().required(),
            corporateName: string().required(),
            industry: string().required(),
            corporateCountryCode: string().required(),
            phoneNumber: string().required()
        }),
        validateOnMount: true,
        onSubmit: (values) => {
            formik.setSubmitting(true);
            const { businessType, industry, email, ...data } = values;
            mutate(data, {
                onSettled: (_, error: AxiosError | undefined | any) => {
                    formik.setSubmitting(false);
                    if (error) {
                        toast.error(
                            getErrorMessage(error) ||
                                'Profile Update failed. Please try again later'
                        );
                        return;
                    }
                    queryClient.invalidateQueries('user-profile');
                    toast.info('Profile Updated successfully');
                    setEditValues(false);
                }
            });
        }
    });

    const [editValues, setEditValues] = useState(false);
    useEffect(() => {
        if (userDetails) {
            formik.setValues({
                firstName: userDetails.firstName || '',
                lastName: userDetails.lastName || '',
                corporateCountryCode: userDetails?.corporate?.countryCode || '',
                industry: userDetails?.corporate?.industry || '',
                businessType: userDetails?.businessTypes || [],
                phoneNumber: userDetails?.phoneNumber || '',
                email: userDetails?.email || '',
                corporateName: userDetails?.corporate?.businessName || '',
                corporateIndustryId:
                    industries.find(
                        (item) => item.name === userDetails?.corporate?.industry
                    )?.id || ''
            });
        }
    }, [userDetails, industries]);

    return (
        <>
            <div className='flex flex-col lg:max-w-[500px] max-w-[100%] w-full mx-auto lg:mx-0 mt-3 gap-[5px]'>
                <div className='flex flex-row justify-between items-center'>
                    <h3 className='color-[#1D2939] text-[16px] font-[500]'>
                        Account Details
                    </h3>
                    {!editValues && (
                        <div
                            className='flex items-center justify-center rounded-[8px] p-[12px] cursor-pointer hover:bg-gray-100'
                            onClick={() => setEditValues(!editValues)}
                        >
                            <EditIcon />
                        </div>
                    )}
                </div>

                <Divider className='my-2' />

                <div className='w-full'>
                    <Input
                        label='First Name'
                        disabled={!editValues}
                        onChange={formik.handleChange}
                        value={formik.values.firstName}
                        name='firstName'
                    />
                </div>

                <div className='w-full mt-2'>
                    <Input
                        label='Last Name'
                        disabled={!editValues}
                        onChange={formik.handleChange}
                        value={formik.values.lastName}
                        name='lastName'
                    />
                </div>

                <div className='mt-2'>
                    <Input
                        label='Email'
                        name='email'
                        disabled
                        leftIcon={
                            <div className='pr-3'>
                                <MessageEnvelope />
                            </div>
                        }
                        value={formik.values.email}
                    />
                </div>
                <div className='mt-2'>
                    <Input
                        label='Business Name'
                        disabled={!editValues}
                        onChange={formik.handleChange}
                        value={formik.values.corporateName}
                        name='corporateName'
                    />
                </div>
                {editValues ? (
                    <div className='mt-2'>
                        <label
                            htmlFor='industry'
                            className='block mb-2 text-sm font-medium text-gray700 text-left'
                        >
                            Industry
                        </label>
                        <Select
                            className={`w-full h-[45px] rounded-[8px] ${
                                editValues
                                    ? 'pointer-events-auto'
                                    : 'pointer-events-none'
                            }`}
                            id='industry'
                            status={
                                !!formik.errors?.businessType
                                    ? 'error'
                                    : undefined
                            }
                            value={formik.values.industry}
                            loading={isBusinessTypesLoading}
                            onChange={(value) => {
                                if (value !== null) {
                                    formik.setFieldValue('industry', value);
                                }
                            }}
                            options={businessTypes.map((type) => ({
                                label: type.name,
                                value: type.name
                            }))}
                        />

                        {!!formik.errors?.businessType}
                        <p className='text-xs text-red100'>
                            {formik.errors?.businessType}
                        </p>
                    </div>
                ) : (
                    <div className='mt-2'>
                        <Input
                            label='Industry'
                            name='industry'
                            disabled={true}
                            value={formik.values.industry}
                        />
                    </div>
                )}

                <div className='mt-2'>
                    <label
                        htmlFor='businessType'
                        className='block mb-2 text-sm font-medium text-gray700 text-left'
                    >
                        Business Type
                    </label>
                    <Select
                        className={`w-full h-[45px] rounded-[8px] ${
                            editValues
                                ? 'pointer-events-auto'
                                : 'pointer-events-none'
                        }`}
                        id='businessType'
                        mode='multiple'
                        status={
                            !!formik.errors?.businessType ? 'error' : undefined
                        }
                        value={formik.values.businessType}
                        loading={isBusinessTypesLoading}
                        onChange={(values) => {
                            if (!editValues) return;
                            if (values !== null) {
                                formik.setFieldValue('businessType', values);
                            }
                        }}
                        options={businessTypes.map((type) => ({
                            label: type.name,
                            value: type.name
                        }))}
                        searchValue={!editValues ? '' : undefined}
                        dropdownStyle={{
                            display: !editValues ? 'none' : undefined
                        }}
                    />

                    {!!formik.errors?.businessType}
                    <p className='text-xs text-red100'>
                        {formik.errors?.businessType}
                    </p>
                </div>

                <div className='w-full mt-2'>
                    {!editValues ? (
                        <Input
                            name='country'
                            disabled={!editValues}
                            label='Country'
                            value={
                                countries.find(
                                    (item) =>
                                        item.countryCode ===
                                        formik.values.corporateCountryCode
                                )?.name
                            }
                        />
                    ) : (
                        <div className='flex gap-4 flex-col sm:flex-row'>
                            <div className='w-full'>
                                <CountriesDropdown
                                    initialValue={
                                        countries?.find(
                                            (item) =>
                                                item.countryCode ===
                                                formik.values
                                                    .corporateCountryCode
                                        )?.name ?? 'Nigeria'
                                    }
                                    error={
                                        formik.touched?.corporateCountryCode &&
                                        !!formik.errors?.corporateCountryCode
                                    }
                                    errorMessage={
                                        formik.touched?.corporateCountryCode
                                            ? formik.errors
                                                  ?.corporateCountryCode
                                            : undefined
                                    }
                                    onHandleChange={(value) => {
                                        if (value !== null) {
                                            formik.setFieldValue(
                                                'corporateCountryCode',
                                                value.countryCode
                                            );
                                        }
                                    }}
                                    {...{
                                        errors: formik.errors,
                                        values: formik.values
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className='w-full mt-2'>
                    <Input
                        label='Phone Number'
                        disabled={!editValues}
                        onChange={formik.handleChange}
                        value={formik.values.phoneNumber}
                        name='phoneNumber'
                    />
                </div>

                {editValues && (
                    <div className='flex flex-row mt-[20px] mb-8 items-center gap-[15px] '>
                        <Button
                            type='button'
                            variant='secondaryOutline'
                            text='Cancel'
                            className='flex-1'
                            onClick={() => {
                                setEditValues(false);
                            }}
                        />

                        <Button
                            type='button'
                            isLoading={formik.isSubmitting}
                            text='Update'
                            className='flex-1'
                            onClick={() => {
                                formik.handleSubmit();
                            }}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default ProfileAccountDetails;
